import React, {
	useEffect,
	useState,
	useCallback,
	useMemo,
	type MouseEvent,
	type KeyboardEvent,
	type SyntheticEvent,
} from 'react';
import { styled } from '@compiled/react';
import FormIcon from '@atlaskit/icon/core/form';
import GlobeIcon from '@atlaskit/icon/core/globe';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import ArchiveIcon from '@atlaskit/icon/core/migration/archive-box--archive';
import BacklogIcon from '@atlaskit/icon/core/migration/backlog';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import ListIcon from '@atlaskit/icon/core/migration/list-bulleted--bullet-list';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import CodeIcon from '@atlaskit/icon/core/migration/angle-brackets--code';
import ComponentIcon from '@atlaskit/icon/core/migration/component';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import ChartIcon from '@atlaskit/icon/core/migration/chart-trend--graph-line';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import LockIcon from '@atlaskit/icon/core/migration/lock-locked--lock';
import RoadmapIcon from '@atlaskit/icon/core/migration/roadmap';
import SettingsIcon from '@atlaskit/icon/core/migration/settings';
import Lozenge from '@atlaskit/lozenge';
import {
	NavigationFooter,
	NavigationHeader,
	NestableNavigationContent,
	Section,
	SideNavigation,
	SkeletonItem,
} from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { isFedRamp } from '@atlassian/atl-context';
import { SCRUM_BOARD } from '@atlassian/jira-common-constants/src/board-types.tsx';
import {
	SOFTWARE_PROJECT,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { CONNECT_ROUTES_SET } from '@atlassian/jira-common-constants/src/spa-routes-sets.tsx';
import { borderRadius, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { AsyncMultiProductUpgradeModal } from '@atlassian/jira-experiments/src/ui/experiments/multi-product-upgrade/async.tsx';
import { useMultiProductUpgrade } from '@atlassian/jira-experiments/src/ui/experiments/multi-product-upgrade/index.tsx';
import { expValEquals, expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { Shortcuts } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/ui/main.tsx';
import { EngagementSpotlight } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/engagement-spotlight/main.tsx';
import { ExpandableSection } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-section/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import {
	NavigationHeaderWrapper,
	NavigationExpandableContentWrapper,
	NavigationContentWrapper,
	ShortCutsAndAppsWrapper,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/navigation-component-wrappers/index.tsx';
import { ScreenReaderMenuHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/screen-reader-header/index.tsx';
import { SpotlightTarget } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import { useBoard } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useJiraSoftwareProjectPagesLink } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project-pages-link/main.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useIncidentExperimentTraits } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/software-incidents/index.tsx';
import {
	setNavModulesCollapseState,
	useNavModulesCollapseState,
	type NavModulesCollapsedType,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/services/nav-modules/index.tsx';
import { RegisterProjectNavigationCommands } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/command-palette/index.tsx';
import {
	ContextualAnalyticsData,
	IssuesContextualAnalyticsData,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { GoalIcon } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/goal-icon/index.tsx';
import { Issues } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/issues/main.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import type { NestingMenuItemProps } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/types.tsx';
import { ProjectHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/project-header/main.tsx';
import { CustomTemplateInlineMessage } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/custom-template-inline-message/main.tsx';
import { ScopeSwitcher } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/scope-switcher/index.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { ConnectMenu } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/ui/connect-menu/main.tsx';
import { useProjectReportsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-reports/index.tsx';
import { useProjectSettingsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-settings/index.tsx';
import { useProjectConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project/index.tsx';
import { useProjectSettingsForgeAppsForMenu } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project-settings/index.tsx';
import { useProjectForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project/index.tsx';
import { ForgeProjectSection } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/project-section/index.tsx';
import { useIsEditableToInline } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/controllers/is-editable-to-inline/index.tsx';
import { InlineConfigurableProjectHeaderAsync } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/ui/async.tsx';
import { OnCallIcon } from '@atlassian/jira-opsgenie-schedule/src/common/ui/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import {
	FireTrackAnalytics,
	FireUiAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/import/file-extension-required -- Required to exclude from SSR
import { JswSideNavRecommendation } from '@atlassian/jira-side-nav-recommendation';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import isIncidentsPageEnabled from '@atlassian/jira-software-incidents-experiment/src/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { LazySuspense } from '@atlassian/react-loosely-lazy';
import type { JiraSpaRoute } from '@atlassian/react-resource-router';
import { MENU_ID, SECTION_ID } from '../../common/constants/index.tsx';
import { useFeatures } from '../../controllers/features/index.tsx';
import type { Features } from '../../controllers/features/utils/types.tsx';
import { DeploymentsMenuItem } from './deployments-menu-item/index.tsx';
import { Footer } from './footer/main.tsx';
import messages from './messages.tsx';
import { ReleasesMenuItem } from './releases-menu-item/index.tsx';
import { Reports } from './reports/main.tsx';
import { Settings } from './settings/main.tsx';
import { handleRapidboardSPAClick } from './utils.tsx';

const isConnectOpen = (route: JiraSpaRoute) => CONNECT_ROUTES_SET.has(route.name);

const isForgeOpen = (route: JiraSpaRoute) =>
	['forge-spa-project', 'project-settings-software-forge-apps'].includes(route.name);

const calculateStack = (
	route: JiraSpaRoute,
	connectStack: null | Array<string>,
	forgeStack: null | Array<string>,
) => {
	if (['legacy-pin', 'issue-navigator', 'issue-navigator-classic'].includes(route.name))
		return [SECTION_ID.ISSUES];

	if (
		[
			'project-settings-automation',
			'project-settings-classic-data-classifications',
			'classic-software-details',
			'classic-software-features',
			'classic-software-link-opsgenie',
			'classic-software-notifications-issue-logs',
			'project-settings-classic-issue-layout',
			'project-settings-software-proforma-project-forms',
			'project-settings-software-toolchain-company-managed',
			'legacy-project-settings-edit',
			'legacy-project-settings-delete',
			'legacy-project-settings-collectors',
			'legacy-project-settings-config',
			'legacy-project-settings-people',
			'legacy-project-settings-fields',
			'legacy-project-settings-issue-layout',
			'legacy-project-settings-issue-security',
			'legacy-project-settings-issue-types',
			'legacy-project-settings-notifications',
			'project-settings-software-notifications-notification-email',
			'legacy-project-settings-opsgenie',
			'legacy-project-settings-permissions',
			'legacy-project-settings-screens',
			'legacy-project-settings-summary',
			'legacy-project-settings-workflows',
			'project-settings-versions',
			'project-settings-components',
			'legacy-project-settings-dev',
		].includes(route.name)
	)
		return [SECTION_ID.SETTINGS];

	if (
		[
			...(fg('reports-overview-page-migration')
				? ['software-reports', 'software-reports-boardless', 'software-reports-velocity-cmp']
				: []),
			'rapidboard-report',
			'legacy-custom-report',
			'legacy-all-reports',
			'legacy-reports-configure',
			'legacy-reports-average-age-report',
			'legacy-reports-created-vs-resolved-report',
			'legacy-reports-pie-report',
			'legacy-reports-recently-created-report',
			'legacy-reports-resolution-time-report',
			'legacy-reports-single-level-group-by-report',
			'legacy-reports-time-since-report',
			'legacy-reports-time-tracking-report',
			'legacy-reports-developer-workload-report',
			'legacy-reports-version-workload-report',
			'legacy-reports-workload-pie-report',
			'software-reports-cycle-time-classic',
			'software-reports-deployment-frequency-classic',
		].includes(route.name)
	)
		return [SECTION_ID.REPORTS];

	if (isConnectOpen(route) && connectStack) {
		return connectStack;
	}

	if (isForgeOpen(route) && forgeStack) {
		return forgeStack;
	}

	return [];
};

const calculateProjectGroupActive = (route: JiraSpaRoute) =>
	[
		'software-roadmap-classic',
		'software-timeline-classic',
		'rapidboard-backlog',
		'legacy-backlog',
		'rapidboard-board',
		'legacy-board',
		...(fg('reports-overview-page-migration')
			? ['software-reports', 'software-reports-boardless', 'software-reports-velocity-cmp']
			: []),
		'rapidboard-report',
		'legacy-custom-report',
		'legacy-all-reports',
		'legacy-reports-configure',
		'legacy-reports-average-age-report',
		'legacy-reports-created-vs-resolved-report',
		'legacy-reports-pie-report',
		'legacy-reports-recently-created-report',
		'legacy-reports-resolution-time-report',
		'legacy-reports-single-level-group-by-report',
		'legacy-reports-time-since-report',
		'legacy-reports-time-tracking-report',
		'legacy-reports-developer-workload-report',
		'legacy-reports-version-workload-report',
		'legacy-reports-workload-pie-report',
		'software-reports-cycle-time-classic',
		'software-list-classic',
	].includes(route.name);

const hasMultipleNavModulesActive = (features: Features | null) =>
	!!features &&
	(features.hasCode ||
		features.hasSecurity ||
		features.hasReleases ||
		features.hasDeployments ||
		features.hasOpsgenieSchedule);

const isReportsIssuesOpen = (route: JiraSpaRoute) =>
	[
		...(fg('reports-overview-page-migration')
			? ['software-reports', 'software-reports-boardless', 'software-reports-velocity-cmp']
			: []),
		'rapidboard-report',
		'legacy-custom-report',
		'legacy-all-reports',
		'legacy-reports-configure',
		'legacy-reports-average-age-report',
		'legacy-reports-created-vs-resolved-report',
		'legacy-reports-pie-report',
		'legacy-reports-recently-created-report',
		'legacy-reports-resolution-time-report',
		'legacy-reports-single-level-group-by-report',
		'legacy-reports-time-since-report',
		'legacy-reports-time-tracking-report',
		'legacy-reports-developer-workload-report',
		'legacy-reports-version-workload-report',
		'legacy-reports-workload-pie-report',
		'software-reports-cycle-time-classic',
		'software-reports-deployment-frequency-classic',
		'legacy-pin',
		'issue-navigator',
		'issue-navigator-classic',
	].includes(route.name);

export const useThrowFeatures = withThrowOnError(useFeatures);
export const useThrowProject = withThrowOnError(useProject);
export const useThrowBoard = withThrowOnError(useBoard);
export const useThrowProjectConnectItems = withThrowOnError(useProjectConnectItems);
export const useThrowProjectReportsConnectItems = withThrowOnError(useProjectReportsConnectItems);
export const useThrowProjectSettingsConnectItems = withThrowOnError(useProjectSettingsConnectItems);
export const useThrowJiraSoftwareProjectPagesLink = withThrowOnError(
	useJiraSoftwareProjectPagesLink,
);

const forgeSettingsStack = [SECTION_ID.SETTINGS];

const ConfigurableProjectHeader = ({
	name,
	avatarUrl,
	projectId,
	description,
}: {
	name: string;
	avatarUrl: string;
	projectId: number;
	description: string;
}) => {
	const shouldShowInlineConfigProjectHeader = useIsEditableToInline();
	return shouldShowInlineConfigProjectHeader() ? (
		<InlineConfigurableProjectHeaderAsync
			key={projectId}
			avatarUrl={avatarUrl}
			description={description}
			name={name}
			projectId={projectId}
			canEdit
		/>
	) : (
		<ProjectHeader avatarUrl={avatarUrl} description={description} name={name} />
	);
};

export const Menu = () => {
	const { formatMessage } = useIntl();
	const route = useCurrentRoute();
	const { data: board } = useThrowBoard();
	const { data: project } = useThrowProject();
	const { data: features } = useThrowFeatures();
	const { data: connectItems, stack: connectProjectStack } = useThrowProjectConnectItems();
	const { data: projectPagesData } = useThrowJiraSoftwareProjectPagesLink();
	const { data: connectReportsItems, stack: connectReportsStack } =
		useThrowProjectReportsConnectItems();
	const { data: connectSettingsItems, stack: connectSettingsStack } =
		useThrowProjectSettingsConnectItems();
	const { stack: forgeProjectStack } = useProjectForgeApps('forge-spa-project');
	const { data: forgeSettingsItems, stack: forgeProjectSettingsStack } =
		useProjectSettingsForgeAppsForMenu('project-settings-software-forge-apps', forgeSettingsStack);
	const navModulesCollapsedState = useNavModulesCollapseState();
	const { isAdmin } = useTenantContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const ecosystemItems = useMemo(
		() => ({
			forge: forgeSettingsItems,
			connect: connectSettingsItems,
		}),
		[connectSettingsItems, forgeSettingsItems],
	);

	const connectStack = connectProjectStack || connectReportsStack || connectSettingsStack;
	const forgeStack = forgeProjectStack || forgeProjectSettingsStack;

	const [stack, setStack] = useState<string[]>(calculateStack(route, connectStack, forgeStack));

	const [projectGroupActive, setProjectGroupActive] = useState<boolean>(
		calculateProjectGroupActive(route),
	);

	const [scrollableNestedMenu, setScrollableNestedMenu] = useState<boolean>(
		stack.includes(SECTION_ID.REPORTS),
	);

	// We need to check we're dealing with a nested menu for a connect item - if we navigate while inside a
	// nested connect item menu or if we're deep linking into a nested menu for a connect item -> we need to maintain the hiding of nav-modules
	const nestedEcosystemAppOpen = useMemo(
		() =>
			(isConnectOpen(route) &&
				!!connectStack &&
				connectStack.length > 0 &&
				!connectStack.includes(SECTION_ID.REPORTS)) ||
			(isForgeOpen(route) && !!forgeStack && forgeStack.length > 0),
		[connectStack, forgeStack, route],
	);

	const [hideExpandableSections, setHideExpandableSections] = useState<boolean>(
		stack.includes(SECTION_ID.SETTINGS) || nestedEcosystemAppOpen,
	);

	const [nestedMenuActive, setNestedMenuOpen] = useState<boolean>(
		stack.includes(SECTION_ID.REPORTS) ||
			stack.includes(SECTION_ID.ISSUES) ||
			stack.includes(SECTION_ID.SETTINGS) ||
			nestedEcosystemAppOpen,
	);

	// Reports and issues were dissappearing when deeplinking when the planning section was collapsed
	// this state controls if the reports or issues items are open, so we can expand the Planning nav module temporarly
	const [reportsIssuesOpen, setReportsIssuesOpen] = useState<boolean>(isReportsIssuesOpen(route));

	const [navModuleCollapseState, setNavModuleCollapseState] = useState<NavModulesCollapsedType>(
		navModulesCollapsedState.classic,
	);

	const [multipleNavModulesActive, setMultipleNavModulesActive] = useState<boolean>(
		hasMultipleNavModulesActive(features),
	);

	const setNavModulesCollapseStateLocalStorage = useCallback(
		(key: string) => {
			const currentState = navModulesCollapsedState;

			const rtnObj = {
				...currentState.classic,
			};

			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ development: { isCollapsed: boolean; }; operations: { isCollapsed: boolean; }; planning: { isCollapsed: boolean; }; }'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'NavModulesCollapsedType'.
			rtnObj[key].isCollapsed = !currentState.classic[key].isCollapsed;

			setNavModulesCollapseState({
				...currentState,
				classic: {
					...rtnObj,
				},
			});

			// Update local state
			setNavModuleCollapseState(rtnObj);
		},
		[navModulesCollapsedState],
	);

	const setNavModulesCollapseStateLocalStoragePlanning = useCallback(
		() => setNavModulesCollapseStateLocalStorage('planning'),
		[setNavModulesCollapseStateLocalStorage],
	);
	const setNavModulesCollapseStateLocalStorageDevelopment = useCallback(
		() => setNavModulesCollapseStateLocalStorage('development'),
		[setNavModulesCollapseStateLocalStorage],
	);
	const setNavModulesCollapseStateLocalStorageOperations = useCallback(
		() => setNavModulesCollapseStateLocalStorage('operations'),
		[setNavModulesCollapseStateLocalStorage],
	);

	const nestedMenuItemOnClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			// Ignore cmd or ctrl clicks - neither of these should set nested menus open
			if (!event.metaKey && !event.ctrlKey) {
				// Wait till event loop finishes before setting nested menu open
				// without this, we get a little bit of jank between issues and
				// ProjectGroupWrapper prior to the transition LN-236
				// *issue presisted when using requestAnimationFrame in lieu of setTimeout
				setTimeout(() => {
					setReportsIssuesOpen(true);
					setNestedMenuOpen(true);
				});
			}
		},
		[setNestedMenuOpen],
	);

	const reportsNestedItemOnClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			// Ignore cmd or ctrl clicks - neither of these should set nested menus open
			if (!event.metaKey && !event.ctrlKey) {
				setTimeout(() => {
					setScrollableNestedMenu(true);
					setNestedMenuOpen(true);
					setReportsIssuesOpen(true);
				});
			}
		},
		[setNestedMenuOpen],
	);

	const nonExpandableSectionNestedOnClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			// Ignore cmd or ctrl clicks - neither of these should set nested menus open
			if (!event.metaKey && !event.ctrlKey) {
				setHideExpandableSections(true);
				// Wait till event loop finishes before setting nested menu open
				// without this, we get a little bit of jank transitioning back to the
				// main sidebar.
				// *issue presisted when using requestAnimationFrame in lieu of setTimeout
				setTimeout(() => setNestedMenuOpen(true));
			}
		},
		[setHideExpandableSections, setNestedMenuOpen],
	);

	useEffect(() => {
		const newStack = calculateStack(route, connectStack, forgeStack);
		setStack(newStack);
		setProjectGroupActive(calculateProjectGroupActive(route));
		setNestedMenuOpen(
			newStack.includes(SECTION_ID.REPORTS) ||
				newStack.includes(SECTION_ID.ISSUES) ||
				newStack.includes(SECTION_ID.SETTINGS) ||
				nestedEcosystemAppOpen,
		);
		setReportsIssuesOpen(isReportsIssuesOpen(route));
		setHideExpandableSections(newStack.includes(SECTION_ID.SETTINGS) || nestedEcosystemAppOpen);
	}, [route, connectStack, forgeStack, nestedEcosystemAppOpen]);

	useEffect(() => {
		setMultipleNavModulesActive(hasMultipleNavModulesActive(features));
	}, [features]);

	const getBackMenuItem = useCallback(
		(preventNavigation = false): NestingMenuItemProps['overrides']['BackMenuItem'] => {
			const isDisabled = __SERVER__ && !board;
			let backUrl = project && !isDisabled ? `/browse/${project.key}` : undefined;
			if (project && board) {
				backUrl = `/jira/software/c/projects/${project.key}/boards/${board.id}`;
			}
			return {
				// @ts-expect-error - 'isDisabled' does not exist in BackMenuItem props
				isDisabled,
				href: backUrl,
				title: formatMessage(messages.backItem),
				onClick: (event: SyntheticEvent) => {
					// on a board-less project we allow to go back without changing url
					// as we don't know where to land :(
					if (!board || preventNavigation) {
						event.preventDefault();
					}
					// Only reset state items if we're _not_ navigating - the navigation will reset these items automatically
					// This also stops some janky behavior in Safari.
					if (preventNavigation) {
						// wait for the event loop to end so in the instance this back menu item appears in the
						// Project group wrapper - without this the blue background appears too soon.
						setTimeout(() => {
							setReportsIssuesOpen(false);
							setNestedMenuOpen(false);
							setScrollableNestedMenu(false);
							setHideExpandableSections(false);
						});
					}
				},
			};
		},
		[
			board,
			project,
			formatMessage,
			setReportsIssuesOpen,
			setNestedMenuOpen,
			setScrollableNestedMenu,
			setHideExpandableSections,
		],
	);

	const multiProductUpgradeProps = useMultiProductUpgrade();

	const { data: incidentExperimentTraits } = useIncidentExperimentTraits();

	if (project == null || features == null || connectItems == null) return <SkeletonItem />;

	const showOperationsSection =
		!isDevopsFeatureDisabledInFedRamp() &&
		(features.hasDeployments ||
			features.hasOpsgenieSchedule ||
			(features.hasIncidents &&
				((incidentExperimentTraits && isIncidentsPageEnabled(incidentExperimentTraits)) ||
					fg('corex_operandi_incidents_page_in_jira'))));

	const showDevelopmentSection =
		!isFedRamp() &&
		(features.hasComponents ||
			features.hasCode ||
			features.hasSecurity ||
			features.hasDevelopment ||
			features.hasReleases);
	const showCodeNavigationLink = !isDevopsFeatureDisabledInFedRamp() && features.hasCode;
	const showSecurityNavigationLink = !isDevopsFeatureDisabledInFedRamp() && features.hasSecurity;
	const showDevelopmentNavigationLink = fg('guava_development_page_in_jira_enabled')
		? !isFedRamp() && features.hasDevelopment && fg('jira-periscope-new-page')
		: !isFedRamp() && fg('jira-periscope-new-page');
	const showIncidentsNavigationLink =
		!isDevopsFeatureDisabledInFedRamp() &&
		features.hasIncidents &&
		((incidentExperimentTraits && isIncidentsPageEnabled(incidentExperimentTraits)) ||
			fg('corex_operandi_incidents_page_in_jira'));

	const projectType = project && toProjectType(project.type);

	const showMultiProductUpgradeModal =
		projectType &&
		projectType === SOFTWARE_PROJECT &&
		!multiProductUpgradeProps.isLoading &&
		multiProductUpgradeProps.isEnrolled;

	const reportsHrefWithBoard = `/jira/software/c/projects/${project.key}/boards/${board?.id}/reports`;
	const reportsHrefBoardless = `/jira/software/c/projects/${project.key}/reports`;

	const reportsNewHref = board ? reportsHrefWithBoard : reportsHrefBoardless;

	const timelineHref = `/jira/software/c/projects/${project.key}/boards/${board?.id}/timeline`;
	const backlogHref = `/jira/software/c/projects/${project.key}/boards/${board?.id}/backlog`;
	const boardHref = `/jira/software/c/projects/${project.key}/boards/${board?.id}`;
	const reportsHref = fg('reports-overview-page-migration')
		? reportsNewHref
		: `/projects/${project.key}?selectedItem=com.atlassian.jira.jira-projects-plugin:report-page`;
	const listHref = `/jira/software/c/projects/${project.key}/list`;
	const calendarHref = `/jira/software/c/projects/${project.key}/boards/${board?.id}/calendar`;
	const summaryHref = `/jira/software/c/projects/${project.key}/summary`;

	const shouldRenderFormItem = () =>
		expValEquals('forms_for_jsw_experiment', 'isEnabled', true) && features.hasForms;

	const ssrPlaceholderEnabledRoutes = [
		...(fg('add_ssr_placeholder_replacements_to_nin_and_nav') ? ['issue-navigator-classic'] : []),
		...(fg('add_ssr_placeholder_replacements_to_nav_for_iv') ? ['issue'] : []),
		...(fg('add_nav_ssr_placeholder_for_board_and_backlog')
			? ['rapidboard-board', 'rapidboard-backlog']
			: []),
	];

	if (fg('issue_terminology_refresh_change_boarding_modal')) {
		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'projectOrIssuePage',
			action: 'viewed',
		});
		fireUIAnalytics(analyticsEvent, 'projectOrIssueScreen');
	}

	return (
		<ContextualAnalyticsData menuId={MENU_ID} stack={stack}>
			<SideNavigation
				label=""
				isServer={__SERVER__}
				isSSRPlaceholderEnabled={ssrPlaceholderEnabledRoutes.includes(route.name)}
			>
				<NavigationHeaderWrapper>
					<NavigationHeader>
						<>
							{fg('jsw_inline_config_project_fg') ? (
								<ConfigurableProjectHeader
									avatarUrl={project.avatarUrl}
									description={formatMessage(messages.headerDescriptionCompanyManaged)}
									name={project.name}
									projectId={project.id}
								/>
							) : (
								<ProjectHeader
									description={formatMessage(messages.headerDescriptionCompanyManaged)}
									avatarUrl={project.avatarUrl}
									name={project.name}
								/>
							)}
							{fg('jira_custom_templates_ux') && (
								<CustomTemplateInlineMessage isLiveTemplate={project.isLiveTemplate} />
							)}
						</>
					</NavigationHeader>
				</NavigationHeaderWrapper>
				<>
					{showMultiProductUpgradeModal && (
						<LazySuspense fallback={null}>
							<AsyncMultiProductUpgradeModal {...multiProductUpgradeProps} />
						</LazySuspense>
					)}
				</>
				<NestableNavigationContent stack={stack} onChange={setStack}>
					<NavigationExpandableContentWrapper
						nestedAppOpen={hideExpandableSections}
						submenuActive={nestedMenuActive}
					>
						<ScreenReaderMenuHeader />
						<ExpandableSection
							context="classic"
							title={multipleNavModulesActive ? formatMessage(messages.planningModule) : undefined}
							submenuActive={nestedMenuActive}
							isCollapsed={navModuleCollapseState.planning.isCollapsed && !reportsIssuesOpen}
							expandClick={setNavModulesCollapseStateLocalStoragePlanning}
							removeTitleSection={!multipleNavModulesActive}
						>
							<ProjectGroupWrapper
								active={projectGroupActive && !nestedMenuActive}
								submenuOpen={nestedMenuActive}
								forceScrollableNestedMenu={scrollableNestedMenu}
								positionForExpandableSection={multipleNavModulesActive}
							>
								<ScopeSwitcher />
								{board && features.hasRoadmap && (
									<MenuItem
										analytics={{ itemId: 'roadmap' }}
										selectedOn={['software-timeline-classic']}
										iconBefore={<RoadmapIcon spacing="spacious" label="" />}
										href={timelineHref}
										testId="navigation-apps-sidebar-software-classic.ui.menu.roadmap-link"
									>
										{formatMessage(messages.timelineItem)}
									</MenuItem>
								)}
								{board && features.hasBacklog && (
									<MenuItem
										analytics={{
											itemId: `backlog-${board.type}`,
										}}
										selectedOn={['rapidboard-backlog', 'legacy-backlog']}
										iconBefore={<BacklogIcon spacing="spacious" label="" />}
										to={backlogHref}
										prefetch="hover"
										onClick={__SPA__ ? undefined : handleRapidboardSPAClick('plan')}
										testId="navigation-apps-sidebar-software-classic.ui.menu.backlog-link"
									>
										{formatMessage(messages.backlogItem)}
									</MenuItem>
								)}
								{board && features.hasBoard && (
									<MenuItem
										analytics={{
											itemId: `board-${board.type}`,
										}}
										selectedOn={['rapidboard-board', 'legacy-board']}
										iconBefore={<BoardIcon spacing="spacious" label="" />}
										to={boardHref}
										prefetch="hover"
										onClick={__SPA__ ? undefined : handleRapidboardSPAClick('work')}
										testId="navigation-apps-sidebar-software-classic.ui.menu.board-link"
									>
										{formatMessage(
											board.type === SCRUM_BOARD ? messages.scrumBoard : messages.kanbanBoard,
										)}
									</MenuItem>
								)}
								{board && features.hasCalendar && (
									<MenuItem
										analytics={{
											itemId: 'calendar',
										}}
										selectedOn={['software-calendar-classic']}
										iconBefore={<CalendarIcon spacing="spacious" label="" />}
										to={calendarHref}
										prefetch="hover"
										testId="navigation-apps-sidebar-software-classic.ui.menu.calendar-link"
									>
										{formatMessage(messages.calendarItem)}
									</MenuItem>
								)}
								<NestingMenuItem
									analytics={{ itemId: 'reports' }}
									href={!fg('reports-overview-page-migration') ? reportsHref : undefined}
									to={fg('reports-overview-page-migration') ? reportsHref : undefined}
									iconBefore={<ChartIcon spacing="spacious" label="" />}
									onClick={reportsNestedItemOnClick}
									id={SECTION_ID.REPORTS}
									overrides={{
										BackMenuItem: getBackMenuItem(true),
									}}
									prefetch={fg('reports-overview-page-migration') ? 'hover' : false}
									selectedOn={[
										...(fg('reports-overview-page-migration')
											? ([
													'software-reports',
													'software-reports-boardless',
													'software-reports-velocity-cmp',
												] as const)
											: []),
										'rapidboard-report',
										'legacy-custom-report',
										'legacy-all-reports',
										'legacy-reports-configure',
										'legacy-reports-average-age-report',
										'legacy-reports-created-vs-resolved-report',
										'legacy-reports-pie-report',
										'legacy-reports-recently-created-report',
										'legacy-reports-resolution-time-report',
										'legacy-reports-single-level-group-by-report',
										'legacy-reports-time-since-report',
										'legacy-reports-time-tracking-report',
										'legacy-reports-developer-workload-report',
										'legacy-reports-version-workload-report',
										'legacy-reports-workload-pie-report',
										'software-reports-cycle-time-classic',
										'software-reports-deployment-frequency-classic',
									]}
									title={formatMessage(messages.reportsItem)}
									testId="navigation-apps-sidebar-software-classic.ui.menu.reports-link"
								>
									<Reports connectItems={connectReportsItems} />
								</NestingMenuItem>
							</ProjectGroupWrapper>
							{/* features.hasSummaryPage is a backend value which includes the evaluation of */}
							{/* `helios_summary_page_experiment` without exposure so we don't want to exclude the users which */}
							{/* evaluates to false from the experiment which is why we place the check at the end */}
							{/* eslint-disable-next-line jira/ff/no-preconditioning */}
							{projectType === SOFTWARE_PROJECT &&
								expVal('helios_summary_page_experiment', 'isEnabled', false) &&
								features.hasSummaryPage && (
									<MenuItem
										analytics={{
											itemId: 'summary',
										}}
										selectedOn={['software-summary-classic']}
										iconBefore={<GlobeIcon label="" color="currentColor" />}
										href={summaryHref}
										data-testid="navigation-apps-sidebar-software-classic.ui.menu.summary-link"
									>
										{formatMessage(messages.summaryItem)}
									</MenuItem>
								)}
							{features.hasList && expValEquals('jsw_list_view', 'cohort', 'variation') && (
								<MenuItem
									analytics={{
										itemId: 'list',
									}}
									selectedOn={['software-list-classic']}
									iconBefore={<ListIcon spacing="spacious" label="" />}
									href={listHref}
									testId="navigation-apps-sidebar-software-classic.ui.menu.list-link"
								>
									{formatMessage(messages.listItem)}
								</MenuItem>
							)}
							{shouldRenderFormItem() && (
								<MenuItem
									analytics={{ itemId: 'forms' }}
									iconBefore={<FormIcon label="" color="currentColor" />}
									href={`/jira/software/c/projects/${project.key}/form`}
									selectedOn={['software-form-classic', 'software-form-builder-classic']}
									data-testid="navigation-apps-sidebar-software-classic.ui.menu.software-forms-link"
								>
									{formatMessage(messages.formsItem)}
								</MenuItem>
							)}
							{features.hasGoals && (
								<MenuItem
									analytics={{ itemId: 'goals' }}
									iconBefore={<GoalIcon />}
									href={`/jira/software/c/projects/${project.key}/goals`}
									selectedOn={['software-goals-classic']}
									data-testid="navigation-apps-sidebar-software-classic.ui.menu.software-goals-link"
								>
									{formatMessage(messages.goalsItem)}
								</MenuItem>
							)}
							{shouldRenderNesting(features.hasIssues, stack, SECTION_ID.ISSUES) && (
								<IssuesContextualAnalyticsData navigatorScope="project">
									<NestingMenuItem
										analytics={{
											itemId: 'project-issue-navigator',
										}}
										href={`/jira/software/c/projects/${project.key}/issues`}
										iconBefore={<IssuesIcon spacing="spacious" label="" />}
										onClick={nestedMenuItemOnClick}
										id={SECTION_ID.ISSUES}
										overrides={{
											BackMenuItem: getBackMenuItem(true),
										}}
										selectedOn={['issue-navigator', 'issue-navigator-classic', 'issue']}
										title={
											<>
												{formatMessage(
													expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
														? messages.ninItemIssueTermRefresh
														: messages.ninItem,
												)}
											</>
										}
										testId="navigation-apps-sidebar-software-classic.ui.menu.issues-link"
									>
										<Issues
											projectKey={project.key}
											pinUrl={`/jira/software/c/projects/${project.key}/issues/?filter=`}
											titleHeaderText={formatMessage(messages.newNinNestedMenuItemTitleHeader)}
										/>
									</NestingMenuItem>
								</IssuesContextualAnalyticsData>
							)}
						</ExpandableSection>

						{showDevelopmentSection && (
							<ExpandableSection
								title={formatMessage(messages.developmentModule)}
								submenuActive={nestedMenuActive}
								isCollapsed={navModuleCollapseState.development.isCollapsed}
								expandClick={setNavModulesCollapseStateLocalStorageDevelopment}
							>
								{features.hasComponents && (
									<MenuItem
										analytics={{ itemId: 'components' }}
										selectedOn={['project-components']}
										iconBefore={<ComponentIcon spacing="spacious" label="" />}
										href={`/jira/software/c/projects/${project.key}/components`}
										testId="navigation-apps-sidebar-software-classic.ui.menu.components-link-development"
									>
										{formatMessage(messages.componentsItem)}
										{fg('jsw_component_link_recommended_lozenge') && (
											<LozengeContainer>
												<Lozenge appearance="new">{formatMessage(messages.recommended)}</Lozenge>
											</LozengeContainer>
										)}
									</MenuItem>
								)}

								{showDevelopmentNavigationLink && (
									<JSErrorBoundary
										id="periscopeMenuItem"
										teamName="nova"
										packageName="jiraNavigationAppsSidebarSoftwareClassic"
									>
										<MenuItem
											analytics={{ itemId: 'periscope' }}
											selectedOn={['software-periscope-classic']}
											iconBefore={<LightbulbIcon label="" color="currentColor" />}
											href={`/jira/software/c/projects/${project.key}/development`}
											testId="navigation-apps-sidebar-software-classic.ui.menu.work-suggestions-link"
										>
											{formatMessage(messages.periscopeItemNonFinal)}
											<LozengeContainer>
												<Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>
											</LozengeContainer>
										</MenuItem>
									</JSErrorBoundary>
								)}
								{showCodeNavigationLink && (
									<>
										<SpotlightTarget name="spotlight-sidebar-code-classic">
											<MenuItem
												analytics={{
													itemId: 'code',
													// @ts-expect-error TS2322 | TS2322 - Type '{ itemId: string; nextgen: true; projectId: any; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'. | Type '{ itemId: string; nextgen: true; projectId: any; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'.
													nextgen: true,
													projectId: project.id,
												}}
												selectedOn={['software-code-classic']}
												iconBefore={<CodeIcon spacing="spacious" label="" />}
												href={`/jira/software/c/projects/${project.key}/code`}
												testId="navigation-apps-sidebar-software-classic.ui.menu.code-link"
											>
												{formatMessage(messages.codeItem)}
											</MenuItem>
											<FireUiAnalytics
												actionSubject="codeNavigationItem"
												attributes={{ nextgen: false }}
												action="viewed"
											/>
										</SpotlightTarget>
										<EngagementSpotlight engagementId="spotlight-sidebar-code-classic" />
									</>
								)}
								{showSecurityNavigationLink && fg('isotopes-security-page-in-jira-enabled-v2') && (
									<MenuItem
										selectedOn={['software-security-classic']}
										iconBefore={<LockIcon spacing="spacious" label="" />}
										href={`/jira/software/c/projects/${project.key}/security`}
										testId="navigation-apps-sidebar-software-classic.ui.menu.security-link"
									>
										{formatMessage(messages.securityItem)}
									</MenuItem>
								)}
								{features.hasReleases && (
									<JSErrorBoundary
										id="releasesMenuItem"
										// Guava Dev Experience (Nova) owns this particular menu item

										teamName="nova"
										packageName="navigationAppsSidebarSoftwareClassic"
									>
										<ReleasesMenuItem
											projectKey={project.key}
											// Note: this test ID is used in a Pollinator check
											testId="navigation-apps-sidebar-software-classic.ui.menu.releases-link"
										/>
									</JSErrorBoundary>
								)}
							</ExpandableSection>
						)}
						{showOperationsSection && (
							<ExpandableSection
								title={formatMessage(messages.operationsModule)}
								submenuActive={nestedMenuActive}
								isCollapsed={navModuleCollapseState.operations.isCollapsed}
								expandClick={setNavModulesCollapseStateLocalStorageOperations}
							>
								{features.hasDeployments && (
									<JSErrorBoundary
										id="deploymentsMenuItem"
										// Fusion Solaris owns this particular menu item

										teamName="fusion-solaris"
										packageName="navigationAppsSidebarSoftwareClassic"
									>
										<DeploymentsMenuItem
											projectKey={project.key}
											// Note: this test ID is used in a Pollinator check
											testId="navigation-apps-sidebar-software-classic.ui.menu.deployments-link"
										/>
									</JSErrorBoundary>
								)}
								{!isFedRamp() && features.hasOpsgenieSchedule && (
									<MenuItem
										analytics={{
											itemId: 'opsgenie',
											// @ts-expect-error TS2322 | TS2322 - Type '{ itemId: string; nextgen: true; projectId: any; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'. | Type '{ itemId: string; nextgen: true; projectId: any; connectionState: DevOpsToolConnectionState | undefined; }' is not assignable to type 'Analytics'.
											nextgen: true,
											projectId: project.id,
										}}
										selectedOn={['opsgenie-schedule']}
										// @ts-expect-error - TS2322 - Type '{ label: string; }' is not assignable to type 'IntrinsicAttributes'.
										iconBefore={<OnCallIcon label="" />}
										href={`/jira/software/c/projects/${project.key}/on-call-schedule`}
										// Note: this test ID is used in a Pollinator check:
										testId="navigation-apps-sidebar-software-classic.ui.menu.on-call-link"
									>
										{formatMessage(messages.opsgenieItem)}
									</MenuItem>
								)}
								{showIncidentsNavigationLink && (
									<MenuItem
										selectedOn={['software-incidents-classic']}
										iconBefore={<ErrorIcon spacing="spacious" label="" />}
										href={`/jira/software/c/projects/${project.key}/incidents`}
										testId="navigation-apps-sidebar-software-classic.ui.menu.incidents-link"
									>
										{formatMessage(messages.incidentsItem)}
										<LozengeContainer>
											<Lozenge appearance="new">{formatMessage(messages.beta)}</Lozenge>
										</LozengeContainer>
									</MenuItem>
								)}
							</ExpandableSection>
						)}
					</NavigationExpandableContentWrapper>
					<NavigationContentWrapper submenuActive={nestedMenuActive}>
						<ShortCutsAndAppsWrapper>
							<Section hasSeparator>
								<Shortcuts
									canAddItem={features.hasShortcutsAdd}
									canAddLinks
									canAddRepositories
									projectKey={project.key}
									projectId={project.id}
									pagesLink={projectPagesData}
									hasPages={features.hasPages}
								>
									<ForgeProjectSection
										nestedMenuActive={nestedMenuActive}
										overrides={{
											BackMenuItem: getBackMenuItem(true),
											ParentMenuItem: {
												onClick: nonExpandableSectionNestedOnClick,
											},
										}}
									/>
									<ConnectMenu
										items={connectItems}
										showIcons
										overrides={{
											BackMenuItem: getBackMenuItem(true),
											// @ts-expect-error - 'ParentMenuItem' does not exist in ConnectMenu props
											ParentMenuItem: {
												onClick: nonExpandableSectionNestedOnClick,
											},
										}}
									/>
								</Shortcuts>
								{shouldRenderNesting(features.hasSettings, stack, SECTION_ID.SETTINGS) && (
									<NestingMenuItem
										analytics={{ itemId: 'settings' }}
										href={`/jira/software/c/projects/${project.key}/settings`}
										iconBefore={<SettingsIcon spacing="spacious" label="" />}
										onClick={nonExpandableSectionNestedOnClick}
										id={SECTION_ID.SETTINGS}
										overrides={{
											BackMenuItem: getBackMenuItem(),
										}}
										selectedOn={['classic-software-details']}
										title={formatMessage(messages.projectSettingsItem)}
										data-test-id="navigation-apps-sidebar-software-classic.ui.menu.project-settings-link"
									>
										<FireTrackAnalytics
											eventName="classicProjectSettingsSidebar viewed"
											attributes={{
												isAdmin,
											}}
										/>
										<Settings ecosystemItems={ecosystemItems} />
									</NestingMenuItem>
								)}
							</Section>
						</ShortCutsAndAppsWrapper>
						{(
							fg('archived_issues_in_nav4_sidebar') ? features?.hasArchivedIssues === true : true
						) ? (
							<Section hasSeparator>
								<MenuItem
									analytics={{ itemId: 'archives' }}
									selectedOn={
										fg('slv-3_migrate_archived_issues_routes')
											? ['software-archived-work-items-classic']
											: ['archived-issues-directory']
									}
									iconBefore={<ArchiveIcon spacing="spacious" label="" />}
									href={
										fg('slv-3_migrate_archived_issues_routes')
											? `/jira/software/c/projects/${project.key}/archived-work-items`
											: `/jira/software/c/projects/${project.key}/issues/archive`
									}
									testId="navigation-apps-sidebar-software-classic.ui.menu.archives-link"
								>
									{formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.itemArchivesIssueTermRefresh
											: messages.itemArchives,
									)}
									<LozengeContainer>
										<Lozenge appearance="new">{formatMessage(messages.new)}</Lozenge>
									</LozengeContainer>
								</MenuItem>
							</Section>
						) : null}
					</NavigationContentWrapper>
				</NestableNavigationContent>
				<NavigationFooter>
					{/* Post Office Side-Nav Recommendations Experiment */}
					<JswSideNavRecommendation />
					<Footer />
				</NavigationFooter>
			</SideNavigation>
			<RegisterProjectNavigationCommands
				timelineHref={timelineHref}
				backlogHref={backlogHref}
				boardHref={boardHref}
				reportsHref={reportsHref}
				features={{
					hasBacklog: board !== null && features.hasBacklog,
					hasBoard: board !== null && features.hasBoard,
					hasReports: board !== null,
					hasRoadmap: board !== null && features.hasRoadmap,
				}}
			/>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LozengeContainer = styled.span({
	marginLeft: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ProjectGroupWrapper = styled.div<{
	submenuOpen: boolean;
	active?: boolean;
	forceScrollableNestedMenu: boolean;
	positionForExpandableSection: boolean;
}>(
	{
		color: 'inherit',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: (props) =>
			(props.submenuOpen && !props.active && !props.forceScrollableNestedMenu) || !props.submenuOpen
				? 'auto'
				: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: (props) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			!props.positionForExpandableSection ? `calc( 100% + ${gridSize}px)` : '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		borderRadius: (props) => (props.submenuOpen ? 0 : `${Math.round(borderRadius * 2.666)}px`),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		padding: (props) => (props.submenuOpen ? 0 : `${gridSize / 2}px`),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		marginBottom: (props) => (props.submenuOpen ? 0 : `${gridSize / 2}px`),
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		marginLeft: (props) => (!props.submenuOpen ? `-${2.125 * gridSize}px` : 0),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"& > a, & > span > a, button[id='navigation-apps.scope-switcher-trigger-button']": {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			paddingLeft: `${1.75 * gridSize}px`,
			backgroundColor: 'transparent',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"&:hover, &[aria-current='page']": {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				backgroundColor: (props) =>
					props.active
						? token('color.background.selected.hovered')
						: token('color.background.selected'),
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"&[aria-current='page']": {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				color: (props) => (props.active ? token('color.text.accent.blue') : undefined),
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > a, & > span > a': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			borderRadius: `${Math.round(borderRadius * 1.333)}px`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"button[id='navigation-apps.scope-switcher-trigger-button']": {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			height: `${gridSize * 5}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"& [data-item-elem-before='true']": {
				display: 'none',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			span: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				color: (props) => (props.active ? token('color.text.selected') : 'inherit'),
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"& span[data-item-description='true']": {
				fontWeight: token('font.weight.regular'),
			},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => props.submenuOpen && "background-color: 'transparent'",
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.submenuOpen && props.active && `background-color: ${token('color.background.selected')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.submenuOpen && !props.active && `background-color: ${token('color.background.neutral')}`,
);
